import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, HeartPulse, Building2, ChevronRight, Trophy, Dumbbell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { captureAttribution } from '../utils/attribution';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const PHLandingPage = () => {
  const { pageSlug } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [landingPage, setLandingPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Capture UTM lead-source attribution on landing
  useEffect(() => {
    captureAttribution();
  }, []);

  useEffect(() => {
    const loadLandingPage = async () => {
      try {
        const response = await axios.get(`${API_URL}/landing-pages/${pageSlug}`);
        setLandingPage(response.data);
      } catch (err) {
        setError('Page not found');
      } finally {
        setLoading(false);
      }
    };
    loadLandingPage();
  }, [pageSlug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#7B6FE8]"></div>
      </div>
    );
  }

  if (error || !landingPage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#1A1A1A] mb-2">Page Not Found</h1>
          <p className="text-[#6B7280]">This landing page doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  const isFitnessFlow = landingPage.flow_template === 'BOUTIQUE_FITNESS_V1';
  const isBothFlow = landingPage.flow_template === 'BOTH_V1';
  const isSportsFlow = !isFitnessFlow && !isBothFlow;

  const handleStartApplication = (track) => {
    const utmParams = {
      utm_source: searchParams.get('utm_source') || landingPage?.utm_source,
      utm_campaign: searchParams.get('utm_campaign') || landingPage?.utm_campaign,
      utm_medium: searchParams.get('utm_medium'),
      utm_content: searchParams.get('utm_content'),
      landing_page_id: landingPage?.id,
      landing_page_slug: landingPage?.slug
    };
    sessionStorage.setItem('ph_landing_source', JSON.stringify(utmParams));
    if (isBothFlow) {
      navigate('/quote/apply');
    } else if (isFitnessFlow) {
      navigate('/quote/fitness');
    } else {
      navigate('/quote/sports');
    }
  };

  const bannerHeadline = landingPage.banner_headline || 'YOUR TRUSTED SPORTS INSURANCE PROVIDER';
  const bannerSubtitle = landingPage.banner_subtitle || "We know sports. We know insurance. Let us handle all of your team's insurance needs. Fill out the form below and get a quote estimate in one business day.";
  const ctaText = landingPage.cta_text || 'Request a Quote Now';
  const dark = landingPage.theme === 'dark';

  return (
    <div className={`min-h-screen ${dark ? 'bg-[#1A1A1A]' : 'bg-white'}`} style={{fontFamily: "'Inter', sans-serif"}}>
      <Helmet>
        <title>{landingPage.headline || 'Insurance Coverage'} | Players Health</title>
        <meta name="description" content={landingPage.subheadline || 'Get specialized insurance coverage for your sports organization or fitness studio from Players Health.'} />
      </Helmet>
      <nav className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b ${dark ? 'bg-[#232323]/95 border-white/10' : 'bg-white/95 border-[#E5E7EB]'}`}>
        <div className="max-w-[1140px] mx-auto px-6 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={dark ? '/logo-light.png' : '/logo-dark.png'} alt="Players Health" className="h-9" />
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#coverage" className={`text-sm font-medium ${dark ? 'text-white/60 hover:text-[#4ECDC4]' : 'text-[#6B7280] hover:text-[#7B6FE8]'} transition-colors`}>Coverage</a>
            <a href="#mission" className={`text-sm font-medium ${dark ? 'text-white/60 hover:text-[#4ECDC4]' : 'text-[#6B7280] hover:text-[#7B6FE8]'} transition-colors`}>Our Mission</a>
            <a href="mailto:support@playershealth.com" className={`text-sm font-medium ${dark ? 'text-white/60 hover:text-[#4ECDC4]' : 'text-[#6B7280] hover:text-[#7B6FE8]'} transition-colors`}>Contact</a>
            <Button 
              onClick={() => handleStartApplication()}
              className="bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] text-white font-bold text-sm uppercase tracking-wider px-7 py-3 h-auto rounded-lg hover:shadow-lg hover:shadow-[#6B5CE7]/30 transition-all hover:-translate-y-0.5"
              style={{fontFamily: "'Barlow Condensed', sans-serif"}}
            >
              Get Quote
            </Button>
          </div>
        </div>
        <div className="h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
      </nav>

      {/* Hero Section */}
      <section className={`relative min-h-[85vh] flex items-center overflow-hidden pt-[72px] ${dark ? 'bg-[#232323]' : 'bg-[#F7F8FA]'}`}>
        <div className={`absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full bg-[#6B5CE7] blur-[160px] ${dark ? 'opacity-[0.12]' : 'opacity-[0.06]'}`}></div>
        <div className={`absolute -bottom-36 -left-24 w-[500px] h-[500px] rounded-full bg-[#4ECDC4] blur-[160px] ${dark ? 'opacity-[0.12]' : 'opacity-[0.06]'}`}></div>

        <div className="relative z-10 max-w-[1140px] mx-auto px-6 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 
                className={`${dark ? 'text-white' : 'text-[#1A1A1A]'} font-black uppercase leading-[0.95] mb-6`} 
                style={{fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(48px, 7vw, 80px)'}}
                data-testid="hero-headline"
              >
                {isBothFlow ? 'Sports & Fitness' : isFitnessFlow ? 'Fitness' : 'Sports'} Insurance<br/>
                <span className="bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] bg-clip-text text-transparent">Made Easy</span>
              </h1>

              <p className={`${dark ? 'text-white/60' : 'text-[#555]'} text-lg max-w-[560px] mb-10 leading-relaxed`}>
                {isBothFlow
                  ? 'Players Health provides specialized insurance for youth sports organizations, amateur leagues, and boutique fitness studios. Get your quote and purchase in minutes!'
                  : isFitnessFlow
                  ? 'Players Health provides specialized insurance for boutique fitness studios, gyms, yoga studios, and personal training businesses. Get your quote and purchase in minutes!'
                  : 'Players Health is here to help you get the best insurance package possible for your youth sports organization. Get your quote and purchase in minutes!'}
              </p>

              <div className="flex flex-wrap gap-4">
                {isBothFlow ? (
                  <Button 
                    onClick={() => handleStartApplication()}
                    data-testid="hero-cta-btn"
                    className="bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] text-white font-bold text-sm uppercase tracking-wider px-8 py-4 h-auto rounded-lg hover:shadow-lg hover:shadow-[#6B5CE7]/35 transition-all hover:-translate-y-0.5"
                    style={{fontFamily: "'Barlow Condensed', sans-serif"}}
                  >
                    {ctaText} <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                ) : (
                  <Button 
                    onClick={() => handleStartApplication()}
                    data-testid="hero-cta-btn"
                    className="bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] text-white font-bold text-sm uppercase tracking-wider px-8 py-4 h-auto rounded-lg hover:shadow-lg hover:shadow-[#6B5CE7]/35 transition-all hover:-translate-y-0.5"
                    style={{fontFamily: "'Barlow Condensed', sans-serif"}}
                  >
                    {ctaText} <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            <div className="hidden lg:block relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-[#6B5CE7]/15">
                <img 
                  src="/hero-tennis.png" 
                  alt="Players Health Hero" 
                  className="w-full h-auto object-cover rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#E040FB] via-[#FF6B35] to-[#FFD93D]"></div>
      </section>

      {/* Value Proposition Banner */}
      <section className={`relative py-12 ${dark ? 'bg-[#2A2A3D]' : 'bg-[#EEF0FF]'}`}>
        <div className="max-w-[1140px] mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className={`${dark ? 'text-white' : 'text-[#1A1A1A]'} font-black text-2xl lg:text-3xl uppercase leading-tight`} style={{fontFamily: "'Barlow Condensed', sans-serif"}}>
                {bannerHeadline}
              </h2>
            </div>
            <div>
              <p className={`${dark ? 'text-white/60' : 'text-[#555]'} leading-relaxed`}>
                {bannerSubtitle}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Mission */}
      <section id="mission" className={`py-24 ${dark ? 'bg-[#1A1A1A]' : 'bg-white'}`}>
        <div className="max-w-[1140px] mx-auto px-6">
          <div className="text-center max-w-[720px] mx-auto">
            <span className={`inline-block ${dark ? 'bg-[#2A2A3D] text-[#A78BFA]' : 'bg-[#EEF0FF] text-[#7B6FE8]'} font-bold text-[11px] uppercase tracking-[2px] px-4 py-1.5 rounded-md mb-5`} style={{fontFamily: "'Barlow Condensed', sans-serif"}}>
              Our Mission
            </span>
            <h2 className={`font-black ${dark ? 'text-white' : 'text-[#1A1A1A]'} uppercase mb-6`} style={{fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(28px, 3.5vw, 42px)'}}>
              Our Mission
            </h2>
            <div className="w-20 h-[3px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] mx-auto rounded-full mb-8"></div>
            <p className={`${dark ? 'text-white/60' : 'text-[#555]'} text-base leading-relaxed`}>
              {isBothFlow
                ? "At Players Health, we're dedicated to protecting the wellbeing of athletes, fitness professionals, and their organizations through comprehensive, tailored insurance solutions. We understand the unique risks in youth sports, amateur leagues, and boutique fitness studios, and we're here to provide peace of mind so you can focus on what matters most."
                : isFitnessFlow
                ? "At Players Health, we're dedicated to protecting the wellbeing of fitness professionals and their businesses through comprehensive, tailored insurance solutions. We understand the unique risks in boutique fitness studios, gyms, and personal training, and we're here to provide peace of mind so you can focus on what matters most—your clients."
                : "At Players Health, we're dedicated to protecting the wellbeing of athletes and organizations through comprehensive, tailored insurance solutions. We understand the unique risks in youth and amateur sports, and we're here to provide peace of mind so you can focus on what matters most—developing athletes and building strong communities."}
            </p>
          </div>
        </div>
      </section>

      {/* Comprehensive Coverage Options */}
      <section id="coverage" className={`py-24 ${dark ? 'bg-[#232323]' : 'bg-[#F7F8FA]'}`}>
        <div className="max-w-[1140px] mx-auto px-6">
          <div className="text-center mb-16">
            <span className={`inline-block ${dark ? 'bg-[#2A2A3D] text-[#A78BFA]' : 'bg-[#EEF0FF] text-[#7B6FE8]'} font-bold text-[11px] uppercase tracking-[2px] px-4 py-1.5 rounded-md mb-5`} style={{fontFamily: "'Barlow Condensed', sans-serif"}}>
              Coverage
            </span>
            <h2 className={`font-black ${dark ? 'text-white' : 'text-[#1A1A1A]'} uppercase mb-4`} style={{fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(28px, 3.5vw, 42px)'}}>
              Comprehensive Coverage Options
            </h2>
            <div className="w-20 h-[3px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] mx-auto rounded-full"></div>
          </div>

          <div className={`grid gap-8 ${isBothFlow ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'}`}>
            {(isSportsFlow || isBothFlow) && (
              <div className={`${dark ? 'bg-[#2A2A3D] border-white/10' : 'bg-white border-[#E5E7EB]'} rounded-2xl p-10 relative overflow-hidden group hover:-translate-y-1 hover:shadow-xl hover:shadow-[#6B5CE7]/10 transition-all border`}>
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="w-14 h-14 rounded-[14px] bg-gradient-to-br from-[#6B5CE7]/10 to-[#4ECDC4]/10 flex items-center justify-center mb-6">
                  <Shield className="h-7 w-7 text-[#7B6FE8]" />
                </div>
                <h3 className={`font-extrabold ${dark ? 'text-white' : 'text-[#1A1A1A]'} text-xl uppercase mb-3 tracking-wide`} style={{fontFamily: "'Barlow Condensed', sans-serif"}}>General Liability</h3>
                <p className={`${dark ? 'text-white/50' : 'text-[#555]'} text-[15px] leading-relaxed`}>
                  Protection against bodily injury, property damage, and personal injury claims. Coverage limits from $1M to $5M available.
                </p>
              </div>
            )}

            {(isSportsFlow || isBothFlow) && (
              <div className={`${dark ? 'bg-[#2A2A3D] border-white/10' : 'bg-white border-[#E5E7EB]'} rounded-2xl p-10 relative overflow-hidden group hover:-translate-y-1 hover:shadow-xl hover:shadow-[#6B5CE7]/10 transition-all border`}>
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="w-14 h-14 rounded-[14px] bg-gradient-to-br from-[#6B5CE7]/10 to-[#4ECDC4]/10 flex items-center justify-center mb-6">
                  <HeartPulse className="h-7 w-7 text-[#4ECDC4]" />
                </div>
                <h3 className={`font-extrabold ${dark ? 'text-white' : 'text-[#1A1A1A]'} text-xl uppercase mb-3 tracking-wide`} style={{fontFamily: "'Barlow Condensed', sans-serif"}}>Participant Accident</h3>
                <p className={`${dark ? 'text-white/50' : 'text-[#555]'} text-[15px] leading-relaxed`}>
                  Medical coverage for athletes injured during practices, games, and travel. Primary and excess coverage options.
                </p>
              </div>
            )}

            {(isFitnessFlow || isBothFlow) && (
              <div className={`${dark ? 'bg-[#2A2A3D] border-white/10' : 'bg-white border-[#E5E7EB]'} rounded-2xl p-10 relative overflow-hidden group hover:-translate-y-1 hover:shadow-xl hover:shadow-[#6B5CE7]/10 transition-all border`}>
                <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <div className="w-14 h-14 rounded-[14px] bg-gradient-to-br from-[#6B5CE7]/10 to-[#4ECDC4]/10 flex items-center justify-center mb-6">
                  <Dumbbell className="h-7 w-7 text-[#4ECDC4]" />
                </div>
                <h3 className={`font-extrabold ${dark ? 'text-white' : 'text-[#1A1A1A]'} text-xl uppercase mb-3 tracking-wide`} style={{fontFamily: "'Barlow Condensed', sans-serif"}}>Professional Liability</h3>
                <p className={`${dark ? 'text-white/50' : 'text-[#555]'} text-[15px] leading-relaxed`}>
                  {isBothFlow
                    ? 'Coverage for fitness professionals against claims of negligence, injury, or improper instruction during classes and training.'
                    : 'Coverage for trainers, instructors, and studio owners against claims of negligence, injury, or improper instruction during classes and sessions.'}
                </p>
              </div>
            )}

            <div className={`${dark ? 'bg-[#2A2A3D] border-white/10' : 'bg-white border-[#E5E7EB]'} rounded-2xl p-10 relative overflow-hidden group hover:-translate-y-1 hover:shadow-xl hover:shadow-[#6B5CE7]/10 transition-all border`}>
              <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] opacity-0 group-hover:opacity-100 transition-opacity"></div>
              <div className="w-14 h-14 rounded-[14px] bg-gradient-to-br from-[#6B5CE7]/10 to-[#4ECDC4]/10 flex items-center justify-center mb-6">
                <Building2 className="h-7 w-7 text-[#7B6FE8]" />
              </div>
              <h3 className={`font-extrabold ${dark ? 'text-white' : 'text-[#1A1A1A]'} text-xl uppercase mb-3 tracking-wide`} style={{fontFamily: "'Barlow Condensed', sans-serif"}}>Property & Equipment</h3>
              <p className={`${dark ? 'text-white/50' : 'text-[#555]'} text-[15px] leading-relaxed`}>
                {isFitnessFlow
                  ? 'Coverage for studio facilities, fitness equipment, and other property owned or leased by your business.'
                  : 'Coverage for facilities, equipment, and other property owned or used by your organization.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="relative py-24 bg-[#232323] overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#6B5CE7]/15 to-[#4ECDC4]/15"></div>
        
        <div className="relative z-10 max-w-[1140px] mx-auto px-6 text-center">
          <h2 className="text-white font-black uppercase mb-5" style={{fontFamily: "'Barlow Condensed', sans-serif", fontSize: 'clamp(28px, 3.5vw, 42px)'}}>
            Ready to Protect<br/>
            <span className="bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] bg-clip-text text-transparent">
              {isBothFlow ? 'Your Organization?' : isFitnessFlow ? 'Your Studio?' : 'Your Team?'}
            </span>
          </h2>
          <p className="text-white/60 max-w-lg mx-auto mb-10 leading-relaxed">
            {isBothFlow
              ? 'Whether you run a sports league or a fitness studio, get a customized quote in one business day. Our team will find the perfect coverage for you.'
              : 'Get a customized quote in one business day. Our team of experts will work with you to find the perfect coverage for your organization.'}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button 
              onClick={() => handleStartApplication()}
              data-testid="cta-btn"
              className="bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4] text-white font-bold text-sm uppercase tracking-wider px-8 py-4 h-auto rounded-lg hover:shadow-lg hover:shadow-[#6B5CE7]/35 transition-all hover:-translate-y-0.5"
              style={{fontFamily: "'Barlow Condensed', sans-serif"}}
            >
              Get Your Quote Now <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] py-8">
        <div className="max-w-[1140px] mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-light.png" alt="Players Health" className="h-7" />
              <span className="text-white/60 text-sm">&copy; 2025 Players Health. All rights reserved.</span>
            </div>
            <div className="flex gap-6">
              <a href="#" className="text-sm text-white/50 hover:text-[#4ECDC4] transition-colors">About</a>
              <a href="mailto:support@playershealth.com" className="text-sm text-white/50 hover:text-[#4ECDC4] transition-colors">Contact</a>
              <a href="#" className="text-sm text-white/50 hover:text-[#4ECDC4] transition-colors">Privacy</a>
              <a href="#" className="text-sm text-white/50 hover:text-[#4ECDC4] transition-colors">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PHLandingPage;
